// -------------------------------
// PF WITHDRAWAL INTERFACES
// -------------------------------
export interface PendingPfWithdrawal {
    pfEmployeeId?: number;
    requestDate?: string;
    amountRequested?: number;
    withdrawalReason?: string;
    attachment?: File | null;
}

export interface PendingDeletePfWithdrawal {
    awaitingId?: boolean;
}

export const startCreatePfWithdrawalFlow = (
    setPending: (data: PendingPfWithdrawal | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    pfEmployeeId: number 
) => {
    setPending({ pfEmployeeId });

    pushAIChat("What is the request date?");
    speak("What is the request date?");
};

export const processPendingPfWithdrawal = (
    text: string,
    pending: PendingPfWithdrawal,
    setPending: (data: PendingPfWithdrawal | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — REQUEST DATE
    if (!pending.requestDate) {
        const date = new Date(text);
        if (isNaN(date.getTime())) {
            pushAIChat("Please provide a valid date.");
            speak("Invalid date.");
            return;
        }

        setPending({
            ...pending,
            requestDate: date.toISOString().split("T")[0]
        });

        pushAIChat("How much amount do you want to withdraw?");
        speak("How much amount do you want to withdraw?");
        return;
    }

    // STEP 2 — AMOUNT
    if (!pending.amountRequested) {
        const amount = Number(text.replace(/[^0-9]/g, ""));
        if (!amount || amount <= 0) {
            pushAIChat("Please provide a valid withdrawal amount.");
            speak("Invalid amount.");
            return;
        }

        setPending({ ...pending, amountRequested: amount });
        pushAIChat("What is the reason for withdrawal?");
        speak("What is the reason for withdrawal?");
        return;
    }

    // STEP 3 — REASON
    if (!pending.withdrawalReason) {
        setPending({ ...pending, withdrawalReason: text });
        pushAIChat("Do you want to attach any document? You can say skip.");
        speak("Do you want to attach any document?");
        return;
    }

    // STEP 4 — ATTACHMENT (OPTIONAL)
    if (pending.attachment === undefined) {
        if (text.toLowerCase().includes("skip")) {
            finalizePfWithdrawal(pending, setPending, pushAIChat, speak);
            return;
        }

        pushAIChat("Please upload the document now or say skip.");
        speak("Please upload the document or say skip.");
        return;
    }
};

const finalizePfWithdrawal = (
    pending: PendingPfWithdrawal,
    setPending: (data: PendingPfWithdrawal | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    pushAIChat("Creating PF withdrawal request...");
    speak("Creating PF withdrawal request");

    window.dispatchEvent(
        new CustomEvent("ai-create-pf-withdrawal", {
            detail: {
                pfEmployeeId: pending.pfEmployeeId,
                requestDate: pending.requestDate,
                amountRequested: pending.amountRequested,
                withdrawalReason: pending.withdrawalReason,
                attachment: pending.attachment || null
            }
        })
    );

    setPending(null);
};

export const startDeletePfWithdrawalFlow = (
    setPending: (data: PendingDeletePfWithdrawal | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({ awaitingId: true });
    pushAIChat("Please provide the PF withdrawal ID to delete.");
    speak("Please provide the PF withdrawal ID.");
};

export const processPendingDeletePfWithdrawal = (
    text: string,
    pending: PendingDeletePfWithdrawal,
    setPending: (data: PendingDeletePfWithdrawal | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingId) return;

    const id = Number(text);
    if (!id) {
        pushAIChat("Please provide a valid withdrawal ID.");
        speak("Invalid withdrawal ID.");
        return;
    }

    pushAIChat(`Deleting PF withdrawal request ${id}...`);
    speak(`Deleting PF withdrawal request ${id}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-pf-withdrawal", {
            detail: { pfWithdrawalId: id }
        })
    );

    setPending(null);
};